import { useEffect, useMemo, useState, lazy, Suspense } from 'react'
import { useI18n, type Locale } from './i18n'
import { AppCtx, type ThemeMode } from './app-context'
import { ErrorBoundary } from './components/ErrorBoundary'
import { loadProfiles, loadActiveId, persistActiveId, persistProfiles } from './store'
import { deleteBaziReading } from './lib/baziReading'
import type { BirthProfile } from './lib/bazi'

const TodayView = lazy(() => import('./views/TodayView'))
const BaziView = lazy(() => import('./views/BaziView'))
const CastView = lazy(() => import('./views/CastView'))
const ProfilesView = lazy(() => import('./views/ProfilesView'))
const SettingsView = lazy(() => import('./views/SettingsView'))

type Tab = 'today' | 'bazi' | 'cast' | 'profiles' | 'settings'

const TABS: { id: Tab; key: string; icon: JSX.Element }[] = [
  { id: 'today', key: 'nav.today', icon: <IconSun /> },
  { id: 'bazi', key: 'nav.bazi', icon: <IconGrid /> },
  { id: 'cast', key: 'nav.cast', icon: <IconHex /> },
  { id: 'profiles', key: 'nav.profiles', icon: <IconUser /> },
  { id: 'settings', key: 'nav.settings', icon: <IconGear /> },
]

export default function App() {
  const { t, locale, setLocale } = useI18n()
  const [tab, setTab] = useState<Tab>('today')
  const [profiles, setProfiles] = useState<BirthProfile[]>([])
  const [activeId, setActiveId] = useState<string | null>(null)
  const [theme, setThemeState] = useState<ThemeMode>(() => {
    const s = localStorage.getItem('dayi.theme')
    return s === 'light' || s === 'dark' || s === 'system' ? s : 'system'
  })
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      const ps = await loadProfiles()
      const aid = await loadActiveId(ps)
      if (!cancelled) {
        setProfiles(ps)
        setActiveId(aid)
        setLoaded(true)
      }
    })()
    return () => { cancelled = true }
  }, [])

  const activeProfile = useMemo(() => profiles.find((p) => p.id === activeId) ?? null, [profiles, activeId])

  const setActiveProfile = (id: string) => {
    setActiveId(id)
    void persistActiveId(id)
  }

  const saveProfile = (p: BirthProfile) => {
    setProfiles((prev) => {
      const exists = prev.some((x) => x.id === p.id)
      const next = exists ? prev.map((x) => (x.id === p.id ? p : x)) : [...prev, p]
      void persistProfiles(next)
      if (!activeId) setActiveId(p.id)
      return next
    })
  }

  const deleteProfile = (id: string) => {
    void deleteBaziReading(id)
    setProfiles((prev) => {
      const next = prev.filter((x) => x.id !== id)
      void persistProfiles(next)
      if (activeId === id) {
        setActiveId(next.length ? next[0].id : null)
        void persistActiveId(next.length ? next[0].id : '')
      }
      return next
    })
  }

  useEffect(() => {
    localStorage.setItem('dayi.theme', theme)
    const apply = () => {
      const dark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches)
      document.documentElement.dataset.theme = dark ? 'dark' : 'light'
      const meta = document.querySelector('meta[name="theme-color"]')
      if (meta) meta.setAttribute('content', dark ? '#141210' : '#f6f1e7')
    }
    apply()
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    mq.addEventListener('change', apply)
    return () => mq.removeEventListener('change', apply)
  }, [theme])

  useEffect(() => {
    document.documentElement.lang = locale === 'zh' ? 'zh-CN' : 'en'
    document.title = locale === 'zh' ? '大易·决策罗盘' : 'Da Yi Compass'
  }, [locale])

  const appValue = useMemo(
    () => ({ profiles, activeProfile, setActiveProfile, saveProfile, deleteProfile, theme, setTheme: setThemeState }),
    [profiles, activeProfile, activeId, theme],
  )

  return (
    <AppCtx.Provider value={appValue}>
      <div className="app-shell">
        <header className="app-header">
          <div className="brand">
            <span className="brand-mark">☰</span>
            <span className="brand-name">{t('app.name')}</span>
          </div>
          <div className="header-actions">
            <LangToggle locale={locale} setLocale={setLocale} />
            <ThemeToggle theme={theme} setTheme={setThemeState} />
          </div>
        </header>

        <main className="app-main">
          {!loaded ? (
            <div className="center-loading">{t('common.loading')}</div>
          ) : (
            <Suspense fallback={<div className="center-loading">{t('common.loading')}</div>}>
              <ErrorBoundary>
              {tab === 'today' && <TodayView onGoToProfiles={() => setTab('profiles')} />}
              {tab === 'bazi' && <BaziView />}
              {tab === 'cast' && <CastView />}
              {tab === 'profiles' && <ProfilesView />}
              {tab === 'settings' && <SettingsView />}
              </ErrorBoundary>
            </Suspense>
          )}
        </main>

        <nav className="tabbar">
          {TABS.map((tb) => (
            <button
              key={tb.id}
              className={`tabbar-item${tab === tb.id ? ' active' : ''}`}
              onClick={() => setTab(tb.id)}
              aria-label={t(tb.key)}
            >
              {tb.icon}
              <span>{t(tb.key)}</span>
            </button>
          ))}
        </nav>
      </div>
    </AppCtx.Provider>
  )
}

function LangToggle({ locale, setLocale }: { locale: Locale; setLocale: (l: Locale) => void }) {
  return (
    <button className="chip-btn" onClick={() => setLocale(locale === 'zh' ? 'en' : 'zh')} aria-label="Switch language">
      {locale === 'zh' ? 'EN' : '中文'}
    </button>
  )
}

function ThemeToggle({ theme, setTheme }: { theme: ThemeMode; setTheme: (t: ThemeMode) => void }) {
  const cycle: ThemeMode[] = ['system', 'light', 'dark']
  const next = cycle[(cycle.indexOf(theme) + 1) % cycle.length]
  return (
    <button className="chip-btn icon-only" onClick={() => setTheme(next)} aria-label="Toggle theme">
      {theme === 'dark' ? <IconMoon /> : theme === 'light' ? <IconSun /> : <IconAuto />}
    </button>
  )
}

function IconSun() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
      <circle cx="12" cy="12" r="4.4" />
      <path d="M12 2.5v2.2M12 19.3v2.2M2.5 12h2.2M19.3 12h2.2M4.9 4.9l1.6 1.6M17.5 17.5l1.6 1.6M19.1 4.9l-1.6 1.6M6.5 17.5l-1.6 1.6" />
    </svg>
  )
}
function IconMoon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
      <path d="M20.5 14.5A8.5 8.5 0 1 1 9.5 3.5a7 7 0 0 0 11 11Z" />
    </svg>
  )
}
function IconAuto() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
      <circle cx="12" cy="12" r="4.4" />
      <path d="M12 2.5v2.2M12 19.3v2.2M2.5 12h2.2M19.3 12h2.2M4.9 4.9l1.6 1.6M17.5 17.5l1.6 1.6M19.1 4.9l-1.6 1.6M6.5 17.5l-1.6 1.6" opacity=".35" />
    </svg>
  )
}
function IconGrid() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
      <rect x="3.5" y="3.5" width="7.4" height="7.4" rx="1.4" />
      <rect x="13.1" y="3.5" width="7.4" height="7.4" rx="1.4" />
      <rect x="3.5" y="13.1" width="7.4" height="7.4" rx="1.4" />
      <rect x="13.1" y="13.1" width="7.4" height="7.4" rx="1.4" />
    </svg>
  )
}
function IconHex() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
      <path d="M3.5 6.5 12 2.5l8.5 4v11L12 21.5l-8.5-4v-11Z" />
      <path d="M3.5 6.5 12 10.5l8.5-4M12 10.5v11" opacity=".5" />
    </svg>
  )
}
function IconUser() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
      <circle cx="12" cy="8.2" r="4" />
      <path d="M4.5 20.5c1.2-3.6 4-5.4 7.5-5.4s6.3 1.8 7.5 5.4" />
    </svg>
  )
}
function IconGear() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
      <circle cx="12" cy="12" r="3.2" />
      <path d="M12 2.8v2.4M12 18.8v2.4M2.8 12h2.4M18.8 12h2.4M5.5 5.5l1.7 1.7M16.8 16.8l1.7 1.7M18.5 5.5l-1.7 1.7M7.2 16.8l-1.7 1.7" />
    </svg>
  )
}
